import { shallowReactive, watch, type Ref } from "vue"

export type ToMap<T, M = T> = (item: T) => M
export type ToArray<T, K extends keyof T, M = T> = (item: M, key: T[K]) => T

/**
 * Keeps a reactive `Map` in sync with a source array (and vice versa).
 *
 * **Caution:** `toMap`'s output must be reference-stable/comparable for a
 * given input, e.g. a primitive or an object reused across calls. The
 * array-to-map sync only re-`set`s a key when `toMap(item) !== <previous value>`;
 * if `toMap` instead returns a freshly-allocated object/array on every call,
 * that comparison is always `true`, causing the array-to-map and map-to-array
 * watchers to keep re-triggering each other indefinitely.
 */
export function useArrayToReactiveMap<T extends object, K extends keyof T, M = T>(
  sourceArray: Ref<T[]>,
  keyName: K,
  toMap: ToMap<T, M>,
  toArray: ToArray<T, K, M>,
) {
  const map = shallowReactive(new Map<T[K], M>)

  watch(sourceArray, (newArr) => {
    const newKeys = new Set()
    newArr.forEach((item) => {
      const key = item[keyName]
      newKeys.add(key)
      const oldItem = map.get(key)
      const newItem = toMap(item)
      if (oldItem !== newItem) {
        map.set(key, newItem)
      }
    })

    map.forEach((_oldItem, oldKey) => {
      if (!newKeys.has(oldKey)) {
        map.delete(oldKey)
      }
    })
  }, { immediate: true })

  watch(map, (newMap) => {
    sourceArray.value = Array.from(
      newMap.entries(),
      ([key, item]) => {
        return toArray(item, key)
      },
    )
  })

  return map
}
